import uniqBy from 'lodash/uniqBy';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useInfiniteQuery, useQueryClient } from 'react-query';

import { useLiveFeedContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LiveFeedContext';
import { fetchTopPostsItems } from 'hb-react/frontoffice/apps/LiveFeed/utils/fetchTopPostsItems';
import { amplitudeLogger } from 'hb-react/shared/utils/amplitude';

const PER_PAGE = 10;

const sortPostsByDate = (a, b) =>
  new Date(b.feedable_at) - new Date(a.feedable_at);

export const TopPostsContext = createContext();

export const TopPostsProvider = ({ children, isCallEnable = false }) => {
  const { config, amplitudeConfig } = useLiveFeedContext();
  const amplitude = amplitudeLogger(amplitudeConfig);
  const queryClient = useQueryClient();

  const [version, setVersion] = useState(0);

  const endpoint = config.routes.top_posts_feed_items_path;
  const queryKey = 'getTopPosts';

  // @see https://tanstack.com/query/v3/docs/react/reference/useInfiniteQuery
  // to know how react-query stores paged data.
  const { data, error, isLoading, fetchNextPage } = useInfiniteQuery(
    queryKey,
    ({ pageParam = 1 }) =>
      fetchTopPostsItems({ endpoint, page: pageParam, perPage: PER_PAGE }),
    {
      enabled: isCallEnable,
      getNextPageParam: (lastPage, allPages) =>
        allPages.length * PER_PAGE < lastPage?.total
          ? allPages.length + 1
          : undefined,
    },
  );

  // Flatten posts from pages into a single list.
  const topPosts = useMemo(() => {
    if (!data?.pages) {
      return [];
    }

    // There is a low chance of desync between FE and BE pages. It least we'll
    // not show the same post twice.
    return uniqBy(
      data.pages
        .reduce((res, page) => res.concat(page.posts), [])
        // Should happen on BE, but since posts could be added from elsewhere,
        // we're making sure that the sorting order always is the same.
        .sort(sortPostsByDate),
      'id',
    );
  }, [data]);

  // Total number of query results returned `total` param within every request.
  const totalItems = useMemo(
    () =>
      Array.isArray(data?.pages) ? data.pages[data.pages.length - 1]?.total : 0,
    [data],
  );

  const onTriggerTopPost = useCallback(
    ({ newItem, topPostHistory = false }) => {
      if (!newItem) {
        return;
      }

      // Update query state accordingly to operation.
      if (newItem.pinned_to_top_posts) {
        queryClient.setQueryData(queryKey, (data) => {
          // Do nothing if query is not positively resolved yet.
          if (!Array.isArray(data?.pages)) {
            return data;
          }

          return {
            ...data,
            pages: data.pages.map(({ posts }, i) => ({
              // Inset new item at the beginning of the first page.
              posts: i === 0 ? [newItem, ...posts] : posts,
              total: totalItems + 1,
            })),
          };
        });
      } else {
        queryClient.setQueryData(queryKey, (data) => {
          if (!data?.pages?.length) {
            return data;
          }

          return {
            ...data,
            pages: data.pages.map(({ posts }) => ({
              // Remove the item from any page.
              posts: posts.filter((topPost) => topPost.id !== newItem.id),
              total: Math.max(totalItems - 1, 0),
            })),
          };
        });
      }

      // Update version if history is needed.
      if (topPostHistory) {
        setVersion(version + 1);
      }
    },
    [queryClient, queryKey, version, totalItems],
  );

  // Track event when: first loaded, user adds/removes top post.
  useEffect(() => {
    if (data) {
      amplitude.track('LiveFeed|ViewedTopPosts', {
        nbTopPosts: totalItems,
      });
    }
  }, [data, totalItems, amplitude]);

  const value = useMemo(
    () => ({
      error,
      isLoading,
      onTriggerTopPost,
      onLoadMore: fetchNextPage,
      topPosts,
      totalItems,
      version,
    }),
    [
      error,
      isLoading,
      fetchNextPage,
      onTriggerTopPost,
      topPosts,
      totalItems,
      version,
    ],
  );

  return (
    <TopPostsContext.Provider value={value}>
      {children}
    </TopPostsContext.Provider>
  );
};

export const useTopPostsContext = () => useContext(TopPostsContext);

export default TopPostsContext;
